import React, { useEffect, useRef, useState } from "react";
import { isPrerendering } from "../utils/isPrerendering";

export default function LazyLoopClip({
  src,
  poster,
  label,
  width,
  height,
  className = "absolute inset-0 h-full w-full object-cover",
  wrapperClassName,
}) {
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const [mediaSrc, setMediaSrc] = useState(null);

  useEffect(() => {
    if (isPrerendering()) return undefined;
    const node = rootRef.current;
    if (!node) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setMediaSrc(src);
      },
      { rootMargin: "120px 0px", threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [src]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !mediaSrc || isPrerendering()) return undefined;

    const tryPlay = () => {
      el.muted = true;
      el.playsInline = true;
      el.play().catch(() => {});
    };
    tryPlay();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) tryPlay();
        else el.pause();
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mediaSrc]);

  return (
    <div ref={rootRef} className={wrapperClassName}>
      <img
        src={poster}
        alt={label}
        width={width}
        height={height}
        className={className}
        decoding="async"
        draggable="false"
      />
      {!mediaSrc ? (
        <span
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
          aria-hidden="true"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/55">
            <span className="ml-0.5 h-0 w-0 border-y-[7px] border-y-transparent border-l-[12px] border-l-white" />
          </span>
        </span>
      ) : null}
      <video
        ref={videoRef}
        className={`${className} z-[1]`}
        src={mediaSrc || undefined}
        poster={poster}
        muted
        loop
        playsInline
        preload="none"
        aria-label={label}
      />
    </div>
  );
}
